import { useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import type { Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, ArrowLeft, ArrowRight, FileSearch, Lock } from "lucide-react";
import type { NicheConfig } from "../../types/niche";
import { contractSchema, emptyContract, isContractField, step1Schema } from "../../lib/contractSchema";
import type { ContractFormData } from "../../lib/contractSchema";
import { evaluateDeadline } from "../../lib/deadline";
import { FormFieldControl } from "./FormFieldControl";
import { ExpiredDeadlineModal } from "./ExpiredDeadlineModal";

export interface FormReturnState {
  step: 1 | 2;
  expiredDays: number | null;
  notice: string | null;
}

interface Props {
  config: NicheConfig;
  defaultValues?: ContractFormData;
  returnState?: FormReturnState | null;
  onSubmit: (data: ContractFormData, days: number) => void;
}

const STEP_TITLES = { 1: "Dados do Contrato", 2: "Qualificação do Titular" } as const;

export function ContractForm({ config, defaultValues, returnState, onSubmit }: Props) {
  const [step, setStep] = useState<1 | 2>(returnState?.step ?? 1);
  const [expiredDays, setExpiredDays] = useState<number | null>(returnState?.expiredDays ?? null);
  const [notice, setNotice] = useState<string | null>(returnState?.notice ?? null);
  const stepRef = useRef(step);
  stepRef.current = step;

  const resolver = useMemo<Resolver<ContractFormData>>(() => {
    const r1 = zodResolver(step1Schema);
    const r2 = zodResolver(contractSchema);
    return (values, ctx, opts) => (stepRef.current === 1 ? r1 : r2)(values, ctx, opts);
  }, []);

  const { control, handleSubmit, watch, setFocus } = useForm<ContractFormData>({
    resolver,
    defaultValues: defaultValues ?? emptyContract,
    mode: "onTouched",
  });
  const values = watch();

  const blockIfExpired = (data: ContractFormData): number | null => {
    const result = evaluateDeadline(data.dataAssinatura);
    if (result.status !== "expired") return result.days;
    setStep(1);
    setExpiredDays(result.days);
    return null;
  };

  const onStep1 = (data: ContractFormData) => {
    setNotice(null);
    if (blockIfExpired(data) !== null) setStep(2);
  };
  const onFinal = (data: ContractFormData) => {
    const days = blockIfExpired(data);
    setNotice(null);
    if (days !== null) onSubmit(data, days);
  };
  const reviewDate = () => {
    setExpiredDays(null);
    window.setTimeout(() => setFocus("dataAssinatura"), 0);
  };

  const fields = config.formFields.filter((f) => {
    if (f.step !== step) return false;
    if (!f.dependsOn || !isContractField(f.dependsOn.field)) return true;
    return values[f.dependsOn.field] === f.dependsOn.value;
  });

  return (
    <form noValidate onSubmit={handleSubmit(step === 1 ? onStep1 : onFinal)} data-testid="contract-form" className="fade-up">
      <div className="mb-8 flex flex-wrap items-baseline justify-between gap-2">
        <h3 data-testid="contract-form-step-title" className="text-lg font-semibold text-slate-100">{STEP_TITLES[step]}</h3>
        <p data-testid="contract-form-step-indicator" className="font-mono text-xs uppercase tracking-[0.16em] text-slate-500">Etapa {step} de 2</p>
      </div>
      <div key={step} className="fade-up grid gap-6 sm:grid-cols-2">
        {fields.map((f) => (isContractField(f.name) ? <FormFieldControl key={f.name} field={f} name={f.name} control={control} /> : null))}
      </div>
      {notice && (
        <p role="alert" data-testid="contract-form-notice" className="mt-8 flex gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
          <AlertCircle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />{notice}
        </p>
      )}
      <div className="mt-10 flex flex-col-reverse gap-3 border-t border-slate-800 pt-8 sm:flex-row sm:items-center sm:justify-between">
        {step === 2 ? (
          <button type="button" onClick={() => setStep(1)} data-testid="contract-form-back-button" className="btn-secondary">
            <ArrowLeft aria-hidden="true" className="h-4 w-4" />Voltar
          </button>
        ) : (
          <p className="flex items-center gap-2 text-xs text-slate-500">
            <Lock aria-hidden="true" className="h-3.5 w-3.5 text-emerald-500" />Dados tratados de forma efêmera, conforme a LGPD.
          </p>
        )}
        {step === 1 ? (
          <button type="submit" data-testid="contract-form-continue-button" className="btn-primary">
            Continuar<ArrowRight aria-hidden="true" className="h-4 w-4" />
          </button>
        ) : (
          <button type="submit" data-testid="contract-form-submit-button" className="btn-primary">
            <FileSearch aria-hidden="true" className="h-4 w-4" />Gerar Prévia
          </button>
        )}
      </div>
      <ExpiredDeadlineModal open={expiredDays !== null} days={expiredDays ?? 0} onReview={reviewDate} />
    </form>
  );
}
